import { ErrorToast, SuccessToast } from '@services/utils/ToastUtils';
import { theme } from '@theme';
import { FC, useEffect, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useCheckDevice } from '../../../hooks/UseCheckDevice';
import { HeaderService } from '../../../services/shared/HeaderService';
import { HelpFormResource } from '../../../types/header/HelpFormResource';
import {
  AttachFileContainer,
  AttachFileLabel,
  AttachFileWrapper,
  ButtonWrapper,
  CustomerDetailWrapper,
  DivWrapper,
  EmailLabel,
  IconWrapper,
  InputBoxWrapper,
  Label,
  NameLabel,
  SupportFormWrapper,
  SupportWrapper,
  TextAreaWrapper,
} from './SupportHelpFormStyles';

export type SupportHelpFormProps = {
  formData?: HelpFormResource;
  closeHelp: () => void;
};

const SupportHelpForm: FC<SupportHelpFormProps> = (props) => {
  const methods = useForm({
    defaultValues: props.formData,
    shouldUseNativeValidation: false,
    mode: 'onChange',
  });

  const { handleSubmit, reset, formState, register, control, getValues, resetField } = methods;

  const [submitted, setSubmitted] = useState<boolean>(false);
  const [fileName, setFileName] = useState<string>('');
  const { isMobileDevice } = useCheckDevice();

  const onFileChange = (event: React.ChangeEvent<HTMLInputElement>, callback): void => {
    const { files } = event.target;
    const selectedFiles = files as FileList;
    const fileSizeLimit = 5 * 1024 * 1024; // 5MB in bytes

    if (selectedFiles[0].size > fileSizeLimit) {
      ErrorToast('File size exceeds the 5MB limit');
      event.target.value = '';
      return;
    }

    callback(selectedFiles[0]);
    setTimeout(() => {
      setFileName(selectedFiles[0].name);
    }, 1);
  };

  const onSubmit = (data: HelpFormResource) => {
    setSubmitted(true);
    const formData = new FormData();

    // format the request object to match the contract that the endpoint expects
    const requestData = {
      firstName: data.firstName,
      lastName: data.lastName,
      phone: data.phone,
      message: data.message,
      sentFromUrl: window.location.href,
      filename: '',
    };

    formData.append('model', JSON.stringify(requestData));

    if (data.file) {
      requestData.filename = data.file.name;
      formData.append('file', data.file);
    }

    HeaderService.postSupportRequest(formData).then(
      () => {
        setTimeout(() => {
          SuccessToast(
            'Notification! \nYour request has been successfully submitted. One of our support specialists will contact you soon.',
          );
        }, 500);
        setSubmitted(false);
        props.closeHelp();
      },
      (err) => {
        setSubmitted(false);
        try {
          if (err.includes('400')) {
            ErrorToast('For security reasons, we cannot accept that file type');
          } else if (err.includes('413')) {
            ErrorToast('File size exceeds the 5MB limit');
          } else {
            let errorMessage = err;
            if (err.includes('429')) {
              errorMessage = err.concat('OK');
            }
            ErrorToast(`'An error has occurred: ${errorMessage}`);
          }
        } catch (error) {
          ErrorToast('For security reasons, we cannot accept this support request');
        }
      },
    );
  };
  const getSupportModal = (): JSX.Element => {
    return (
      <>
        <div className='row'>
          <DivWrapper data-testid='first-name' className='col-sm-6'>
            <InputBoxWrapper
              paddingTopBottom='0'
              paddingLeftRight='15'
              height='45'
              type={'text'}
              disabled={true}
              {...register('firstName')}
            />
          </DivWrapper>
          <DivWrapper data-testid='last-name' className='col-sm-6'>
            <InputBoxWrapper
              paddingTopBottom='0'
              paddingLeftRight='15'
              height='45'
              type={'text'}
              disabled={true}
              {...register('lastName')}
            />
          </DivWrapper>
        </div>
      </>
    );
  };

  const getSupportMobileModal = (): JSX.Element => {
    return (
      <CustomerDetailWrapper data-testid='customer-detail'>
        <div className='row'>
          <DivWrapper className='support-from'>
            <Label fontWeight={theme.typography.fontWeights.semibold} color={theme.palette.dusty}>
              FROM:
            </Label>
          </DivWrapper>
          <DivWrapper marginLeft='10' data-testid='details-container' className='support-email'>
            <NameLabel
              fontWeight={theme.typography.fontWeights.semibold}
              color={theme.palette.basicGray}
            >
              {props.formData?.firstName}&nbsp;{props.formData?.lastName}
            </NameLabel>
            <div>
              <EmailLabel
                fontWeight={theme.typography.fontWeights.normal}
                color={theme.palette.basicGray}
              >
                ({props.formData?.email})
              </EmailLabel>
            </div>
          </DivWrapper>
        </div>
      </CustomerDetailWrapper>
    );
  };

  useEffect(() => {
    reset();
  }, [props.formData]);

  return (
    <SupportWrapper data-testid='support-help-form'>
      <FormProvider {...methods}>
        <SupportFormWrapper
          data-testid='support-help-form-wrapper'
          height={`${isMobileDevice ? '60vh' : ''}`}
          padding={`${isMobileDevice ? '35' : '45'}`}
          encType='multipart/form-data'
          onSubmit={handleSubmit(onSubmit)}
        >
          {!isMobileDevice ? getSupportModal() : getSupportMobileModal()}
          <div className='row'>
            {!isMobileDevice && (
              <DivWrapper className='col-sm-6'>
                <InputBoxWrapper
                  paddingTopBottom='0'
                  paddingLeftRight='15'
                  height='45'
                  type='email'
                  disabled={true}
                  {...register('email')}
                ></InputBoxWrapper>
              </DivWrapper>
            )}
            <DivWrapper className={isMobileDevice ? '' : 'col-sm-6'}>
              <InputBoxWrapper
                paddingTopBottom='0'
                paddingLeftRight='15'
                height='45'
                isMobileDevice={isMobileDevice}
                type='text'
                placeholder='Phone'
                {...register('phone', phoneValidation)}
              ></InputBoxWrapper>
            </DivWrapper>
          </div>
          <TextAreaWrapper
            isMobileDevice={isMobileDevice}
            cols={20}
            placeholder='Message'
            rows={2}
            {...register('message', messageValidation)}
          ></TextAreaWrapper>
          <AttachFileWrapper fontSize={isMobileDevice ? 'large' : theme.typography.h5 + 'px'}>
            <IconWrapper marginRight='15' type='add'></IconWrapper>
            <Controller
              control={control}
              name={'file'}
              render={({ field: { value, onChange, ...field } }) => {
                return (
                  <>
                    <AttachFileContainer
                      {...field}
                      value={undefined}
                      onChange={(event) => {
                        onFileChange(event, onChange);
                      }}
                      id='attach-file-browse1'
                      type='file'
                      accept='.xlsx,.csv,.pdf,.png,.jpg,.doc,.txt,.docx'
                    />
                    <label htmlFor='attach-file-browse1'>Attach a file</label>
                  </>
                );
              }}
            />
          </AttachFileWrapper>
          <AttachFileLabel fontSize={isMobileDevice ? 'large' : theme.typography.h5 + 'px'}>
            {getValues('file')?.name}&nbsp;
            {getValues('file')?.name && (
              <IconWrapper
                type='x'
                fontSize={isMobileDevice ? '135' : '110'}
                verticalAlign='middle'
                onClick={(event) => {
                  resetField('file');
                  event.stopPropagation();
                }}
                data-testid='remove-file'
              ></IconWrapper>
            )}
          </AttachFileLabel>
          <p
            style={{
              color: theme.palette.basicGray,
              fontSize: '12px',
              fontWeight: theme.typography.fontWeights.normal,
              marginTop: '-5px',
            }}
          >
            Supported formats: .xlsx, .csv, .pdf, .png, .jpg, .doc, .txt, .docx. Max size: 5MB.
          </p>

          <ButtonWrapper>
            <InputBoxWrapper
              isButton={true}
              isMobileDevice={isMobileDevice}
              paddingTopBottom='6'
              paddingLeftRight='12'
              type={'submit'}
              disabled={submitted || !formState.isDirty || !formState.isValid}
              className={`submit btn btn-primary`}
              value={'Send'}
              id='btnquestionsend'
            />
          </ButtonWrapper>
        </SupportFormWrapper>
      </FormProvider>
    </SupportWrapper>
  );
};

export default SupportHelpForm;

const phoneValidation = {
  required: {
    value: true,
    message: 'required',
  },
};

const messageValidation = {
  required: {
    value: true,
    message: 'required',
  },
};
